import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../services/api';
import { ArrowLeft, Plus, Edit2, Trash2, CreditCard } from 'lucide-react';
import ArborescenceLogo from '../../../components/ArborescenceLogo';
import '../../../styles/admin/ERPPaymentModalities.css';


const ERPPaymentModalities = () => {
  const [modalities, setModalities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingModality, setEditingModality] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    code: '',
    description: '',
    type_modalite: 'immediate',
    pourcentage_premier_versement: 100,
    date_deuxieme_versement: '2026-07-01',
    instructions_virement: '',
    actif: true,
    ordre: 0
  });

  useEffect(() => {
    fetchModalities();
  }, []);

  const fetchModalities = async () => {
    try {
      const response = await api.get(
        '/api/erp/payment-modalities',
      );
      setModalities(response.data.modalities || []);
      setLoading(false);
    } catch (error) {
      alert('Erreur lors du chargement des modalités');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingModality) {
        await api.put(
          `/api/erp/payment-modalities/${editingModality.id}`,
          formData,
        );
      } else {
        await api.post(
          '/api/erp/payment-modalities',
          formData,
        );
      }

      setShowModal(false);
      setEditingModality(null);
      resetForm();
      fetchModalities();
    } catch (error) {
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (modality) => {
    setEditingModality(modality);
    setFormData({
      nom: modality.nom,
      code: modality.code,
      description: modality.description || '',
      type_modalite: modality.type_modalite,
      pourcentage_premier_versement: modality.pourcentage_premier_versement || 100,
      date_deuxieme_versement: modality.date_deuxieme_versement || '2026-07-01',
      instructions_virement: modality.instructions_virement || '',
      actif: modality.actif,
      ordre: modality.ordre || 0
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette modalité de paiement?')) return;

    try {
      await api.delete(
        `/api/erp/payment-modalities/${id}`,
      );
      fetchModalities();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleSeedDefault = async () => {
    if (!window.confirm('Créer les 4 modalités par défaut?')) return;

    try {
      await api.post(
        '/api/erp/payment-modalities/seed-default',
        {},
      );
      fetchModalities();
      alert('✅ Modalités par défaut créées!');
    } catch (error) {
      alert('Les modalités par défaut existent déjà ou erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      code: '',
      description: '',
      type_modalite: 'immediate',
      pourcentage_premier_versement: 100,
      date_deuxieme_versement: '2026-07-01',
      instructions_virement: '',
      actif: true,
      ordre: 0
    });
  };

  const getModalityIcon = (type) => {
    switch (type) {
      case 'immediate': return '💳';
      case 'split_fixed_date': return '📅';
      case 'bank_transfer': return '🏦';
      case 'credit_card': return '💵';
      default: return '💰';
    }
  };

  const getModalityTypeLabel = (type) => {
    switch (type) {
      case 'immediate': return 'Paiement immédiat';
      case 'split_fixed_date': return 'Versement avec date fixe';
      case 'bank_transfer': return 'Virement bancaire';
      case 'credit_card': return 'Carte de crédit';
      default: return type;
    }
  };

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="erp-payment-modalities-page">
      <div className="page-header">
        <div className="page-header-content">
          <Link to="/admin/erp" className="back-link">
            <ArrowLeft size={20} />
            Retour au dashboard ERP
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
            <ArborescenceLogo size="small" showText={false} />
            <div>
              <h1>💳 Modalités de paiement</h1>
              <p>Configurez les options de paiement pour vos clients</p>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="page-actions">
          <button className="btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
            <Plus size={20} />
            Nouvelle modalité
          </button>
          {modalities.length === 0 && (
            <button className="btn-secondary" onClick={handleSeedDefault}>
              <CreditCard size={20} />
              Créer les modalités par défaut
            </button>
          )}
        </div>

        <div className="modalities-grid">
          {modalities.length === 0 ? (
            <div className="empty-state">
              <CreditCard size={64} color="#ccc" />
              <p>Aucune modalité de paiement configurée</p>
              <button className="btn-primary" onClick={handleSeedDefault}>
                Créer les modalités par défaut
              </button>
            </div>
          ) : (
            modalities.map(modality => (
              <div key={modality.id} className={`modality-card ${!modality.actif ? 'inactive' : ''}`}>
                <div className="modality-card-header">
                  <div className="modality-icon">{getModalityIcon(modality.type_modalite)}</div>
                  <div className="modality-info">
                    <h3>{modality.nom}</h3>
                    <span className="modality-type">{getModalityTypeLabel(modality.type_modalite)}</span>
                  </div>
                  <div className="modality-actions">
                    <button className="btn-icon" onClick={() => handleEdit(modality)} title="Modifier">
                      <Edit2 size={18} />
                    </button>
                    <button className="btn-icon delete" onClick={() => handleDelete(modality.id)} title="Supprimer">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                
                <div className="modality-card-body">
                  {modality.description && (
                    <p className="modality-description">{modality.description}</p>
                  )}
                  
                  {modality.type_modalite === 'split_fixed_date' && (
                    <div className="modality-details">
                      <div className="detail-item">
                        <strong>Premier versement:</strong> {modality.pourcentage_premier_versement}%
                      </div>
                      <div className="detail-item">
                        <strong>Date 2e versement:</strong> {new Date(modality.date_deuxieme_versement).toLocaleDateString('fr-CA')}
                      </div>
                    </div>
                  )}
                  
                  {modality.type_modalite === 'bank_transfer' && modality.instructions_virement && (
                    <div className="modality-details">
                      <strong>Instructions:</strong>
                      <pre className="bank-instructions">{modality.instructions_virement}</pre>
                    </div>
                  )}
                  
                  <div className="modality-status">
                    {modality.actif ? (
                      <span className="badge active">✓ Active</span>
                    ) : (
                      <span className="badge inactive">✗ Inactive</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingModality ? 'Modifier la modalité' : 'Nouvelle modalité'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="modality-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    required
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    placeholder="Ex: Paiement comptant"
                  />
                </div>
                <div className="form-group">
                  <label>Code *</label>
                  <input
                    type="text"
                    required
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    placeholder="Ex: paiement_comptant"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="2"
                  placeholder="Description de la modalité..."
                />
              </div>

              <div className="form-group">
                <label>Type de modalité *</label>
                <select
                  value={formData.type_modalite}
                  onChange={(e) => setFormData({...formData, type_modalite: e.target.value})}
                  required
                >
                  <option value="immediate">Paiement immédiat (100%)</option>
                  <option value="split_fixed_date">Versement avec date fixe</option>
                  <option value="bank_transfer">Virement bancaire</option>
                  <option value="credit_card">Carte de crédit</option>
                </select>
              </div>

              {formData.type_modalite === 'split_fixed_date' && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Pourcentage 1er versement (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.pourcentage_premier_versement}
                      onChange={(e) => setFormData({...formData, pourcentage_premier_versement: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Date du 2e versement</label>
                    <input
                      type="date"
                      value={formData.date_deuxieme_versement}
                      onChange={(e) => setFormData({...formData, date_deuxieme_versement: e.target.value})}
                    />
                  </div>
                </div>
              )}

              {formData.type_modalite === 'bank_transfer' && (
                <div className="form-group">
                  <label>Instructions pour virement</label>
                  <textarea
                    value={formData.instructions_virement}
                    onChange={(e) => setFormData({...formData, instructions_virement: e.target.value})}
                    rows="6"
                    placeholder="Banque: [Nom de votre banque]&#10;Numéro de compte: [XXXXX]&#10;Numéro de transit: [XXXXX]&#10;Institution: [XXX]&#10;&#10;Référence: Mentionner le numéro de facture"
                  />
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.actif}
                      onChange={(e) => setFormData({...formData, actif: e.target.checked})}
                    />
                    {' '}Modalité active
                  </label>
                </div>
                <div className="form-group">
                  <label>Ordre d'affichage</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.ordre}
                    onChange={(e) => setFormData({...formData, ordre: parseInt(e.target.value)})}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingModality ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ERPPaymentModalities;
